import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { LayoutGrid, Rocket, Heart, Bell, Settings, TrendingUp, DollarSign, Users, Lock, Pencil, Loader2, LifeBuoy, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import CampaignCard from "@/components/CampaignCard";
import ProgressBar from "@/components/ProgressBar";
import EditProfileModal from "@/components/EditProfileModal";
import { useAuth } from "@/context/AuthContext";
import { fetchMyCampaigns, fetchMyPledges, fetchMyTickets, createTicket } from "@/lib/api";
import { toast } from "sonner";

const Dashboard = () => {
  const [tab, setTab] = useState("overview");
  const { user, loading: authLoading, openAuth } = useAuth();
  const [myCampaigns, setMyCampaigns] = useState([]);
  const [pledges, setPledges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [tSubject, setTSubject] = useState("");
  const [tMessage, setTMessage] = useState("");
  const [tBusy, setTBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    Promise.all([fetchMyCampaigns().catch(() => []), fetchMyPledges().catch(() => []), fetchMyTickets().catch(() => [])])
      .then(([cs, ps, ts]) => { setMyCampaigns(cs); setPledges(ps); setTickets(ts); })
      .finally(() => setLoading(false));
  }, [user]);

  const submitTicket = async (e) => {
    e.preventDefault();
    if (!tSubject.trim() || !tMessage.trim()) return;
    setTBusy(true);
    try {
      await createTicket({ subject: tSubject, message: tMessage });
      toast.success("Ticket submitted — we'll respond shortly");
      setTSubject(""); setTMessage("");
      const ts = await fetchMyTickets();
      setTickets(ts);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setTBusy(false); }
  };

  if (authLoading) return <div className="min-h-[60vh] grid place-items-center text-navy-500" data-testid="dashboard-loading"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  if (!user) {
    return (
      <div className="min-h-[70vh] grid place-items-center bg-cream-100/70" data-testid="dashboard-gate">
        <div className="max-w-md text-center px-6">
          <div className="mx-auto inline-grid place-items-center h-14 w-14 rounded-2xl bg-saffron-50 text-saffron-600"><Lock className="h-6 w-6" /></div>
          <h1 className="mt-6 font-display text-3xl font-semibold tracking-tight text-navy-800">Sign in to your dashboard.</h1>
          <p className="mt-3 text-navy-600">Track your campaigns, backed projects, saved lists, and notifications.</p>
          <div className="mt-6 flex justify-center gap-2">
            <Button data-testid="dashboard-signin-btn" onClick={() => openAuth("login", "Sign in to view your dashboard.")} className="rounded-2xl bg-saffron-600 hover:bg-saffron-700 h-11 px-6">Sign in</Button>
            <Button variant="outline" onClick={() => openAuth("register", "Create an account to get started.")} className="rounded-2xl h-11 px-6">Create account</Button>
          </div>
        </div>
      </div>
    );
  }

  const firstName = user.name?.split(" ")[0] || "there";
  const totalRaised = myCampaigns.reduce((acc, c) => acc + (c.raised || 0), 0);
  const totalBackers = myCampaigns.reduce((acc, c) => acc + (c.backers || 0), 0);
  const totalPledged = pledges.reduce((acc, p) => acc + (p.amount || 0), 0);
  const backedCampaigns = pledges.map((p) => p.campaign).filter(Boolean);

  return (
    <div data-testid="dashboard-page" className="bg-cream-100/70 min-h-screen">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <span className="text-sm font-medium text-saffron-600">Dashboard</span>
            <h1 className="mt-1 font-display text-4xl font-semibold tracking-tight text-navy-800">Welcome back, {firstName}.</h1>
            <p className="mt-1 text-navy-500">Here's what's happening across your projects.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="dashboard-edit-profile-btn" className="rounded-2xl h-11">
              <Pencil className="h-4 w-4 mr-2" /> Edit profile
            </Button>
            <Button asChild className="rounded-2xl bg-saffron-600 hover:bg-saffron-700 h-11">
              <Link to="/start">Start new campaign</Link>
            </Button>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="mt-10">
          <TabsList className="rounded-2xl bg-white border border-warm-200 p-1 h-auto flex-wrap">
            {[
              { v: "overview", l: "Overview", i: LayoutGrid },
              { v: "campaigns", l: "My campaigns", i: Rocket },
              { v: "backed", l: "Backed", i: Heart },
              { v: "saved", l: "Saved", i: Heart },
              { v: "notifications", l: "Notifications", i: Bell },
              { v: "support", l: "Support", i: LifeBuoy },
              { v: "settings", l: "Settings", i: Settings },
            ].map((t) => (
              <TabsTrigger key={t.v} value={t.v} data-testid={`dashboard-tab-${t.v}`}
                className="rounded-xl px-4 py-2 data-[state=active]:bg-saffron-600 data-[state=active]:text-white gap-2">
                <t.i className="h-4 w-4" /> {t.l}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="overview" className="mt-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { l: "Total raised (yours)", v: `$${totalRaised.toLocaleString()}`, i: DollarSign, color: "text-emerald-600 bg-emerald-50" },
                { l: "Total backers", v: totalBackers.toLocaleString(), i: Users, color: "text-saffron-600 bg-saffron-50" },
                { l: "You pledged", v: `$${totalPledged.toLocaleString()}`, i: TrendingUp, color: "text-forest-700 bg-forest-500/15" },
              ].map((k, i) => (
                <div key={i} className="rounded-2xl border border-warm-200 bg-white p-6">
                  <div className={`inline-grid place-items-center h-10 w-10 rounded-xl ${k.color}`}><k.i className="h-5 w-5" /></div>
                  <div className="mt-4 font-display text-3xl font-semibold text-navy-800">{k.v}</div>
                  <div className="text-sm text-navy-500 mt-0.5">{k.l}</div>
                </div>
              ))}
            </div>

            {myCampaigns.length > 0 && (
              <div className="mt-8 rounded-2xl border border-warm-200 bg-white p-6">
                <h3 className="font-display text-lg font-semibold">Live campaign progress</h3>
                <div className="mt-6 space-y-6">
                  {myCampaigns.map((c) => {
                    const percent = Math.round((c.raised / c.goal) * 100);
                    return (
                      <div key={c.id} data-testid={`overview-campaign-${c.id}`}>
                        <div className="flex items-center justify-between mb-2">
                          <Link to={`/campaign/${c.id}`} className="font-medium text-navy-800 hover:text-saffron-600 transition-colors">{c.title}</Link>
                          <span className="text-sm text-navy-500">{percent}% · ${c.raised.toLocaleString()}</span>
                        </div>
                        <ProgressBar value={Math.min(percent, 100)} />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="campaigns" className="mt-8">
            {loading ? <div className="text-navy-500">Loading...</div> : myCampaigns.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 p-12 text-center">
                <div className="text-navy-500">You haven't launched a campaign yet.</div>
                <Button asChild className="mt-4 rounded-2xl bg-saffron-600 hover:bg-saffron-700"><Link to="/start">Start your first campaign</Link></Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {myCampaigns.map((c, i) => <CampaignCard key={c.id} campaign={c} index={i} />)}
              </div>
            )}
          </TabsContent>

          <TabsContent value="backed" className="mt-8">
            {loading ? <div className="text-navy-500">Loading...</div> : backedCampaigns.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 p-12 text-center text-navy-500">You haven't backed any projects yet. <Link to="/explore" className="text-saffron-600 font-medium hover:underline">Explore campaigns</Link></div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {backedCampaigns.map((c, i) => c && <CampaignCard key={`${c.id}-${i}`} campaign={c} index={i} />)}
              </div>
            )}
          </TabsContent>

          <TabsContent value="saved" className="mt-8">
            <div className="rounded-2xl border border-dashed border-slate-300 p-12 text-center text-navy-500">
              Bookmark projects while browsing — they'll appear here.
            </div>
          </TabsContent>

          <TabsContent value="notifications" className="mt-8">
            <div className="rounded-2xl border border-warm-200 bg-white divide-y divide-slate-100">
              {pledges.length === 0 ? (
                <div className="p-8 text-center text-navy-500">No notifications yet.</div>
              ) : pledges.slice(0, 5).map((p, i) => (
                <div key={i} className="flex items-start gap-3 p-5">
                  <span className="mt-1 h-2 w-2 rounded-full bg-emerald-500 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="font-medium text-navy-800">You pledged ${p.amount} to {p.campaign?.title || "a campaign"}</div>
                    <div className="text-xs text-navy-500 mt-0.5">{new Date(p.created_at).toLocaleDateString()}</div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="support" className="mt-8">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6" data-testid="user-support-tab">
              <form onSubmit={submitTicket} className="lg:col-span-2 rounded-2xl border border-warm-200 bg-white p-6" data-testid="submit-ticket-form">
                <h3 className="font-display text-lg font-semibold">Submit a ticket</h3>
                <p className="text-sm text-navy-500 mt-1">Our team responds within 1 business day.</p>
                <div className="mt-4 space-y-3">
                  <Input value={tSubject} onChange={(e) => setTSubject(e.target.value)} placeholder="Subject" required
                    data-testid="ticket-subject-input" className="h-11 rounded-xl border-warm-200" />
                  <Textarea value={tMessage} onChange={(e) => setTMessage(e.target.value)} placeholder="Describe your issue…" required rows={6}
                    data-testid="ticket-message-input" className="rounded-xl border-warm-200" />
                  <Button type="submit" disabled={tBusy} data-testid="ticket-submit-btn"
                    className="w-full rounded-2xl bg-saffron-600 hover:bg-saffron-700 h-11">
                    {tBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-2" /> Send ticket</>}
                  </Button>
                </div>
              </form>
              <div className="lg:col-span-3 rounded-2xl border border-warm-200 bg-white p-6">
                <h3 className="font-display text-lg font-semibold">Your tickets</h3>
                {tickets.length === 0 ? (
                  <div className="mt-6 text-sm text-navy-500">No tickets yet.</div>
                ) : (
                  <ul className="mt-4 divide-y divide-slate-100">
                    {tickets.map((t) => (
                      <li key={t.ticket_id} className="py-4" data-testid={`my-ticket-${t.ticket_id}`}>
                        <div className="flex items-center justify-between">
                          <div className="font-medium">{t.subject}</div>
                          <span className={`text-xs rounded-full px-2 py-0.5 ${t.status === "resolved" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{t.status}</span>
                        </div>
                        <div className="text-xs text-navy-500 mt-0.5">{new Date(t.created_at).toLocaleString()} · {t.messages.length} message{t.messages.length > 1 ? "s" : ""}</div>
                        {t.messages.slice(-1).map((m, i) => (
                          <div key={i} className="mt-2 text-sm text-navy-600 line-clamp-2">
                            <span className={`text-[10px] uppercase tracking-widest mr-2 ${m.from === "admin" ? "text-saffron-600 font-semibold" : "text-slate-400"}`}>{m.from}</span>{m.message}
                          </div>
                        ))}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="settings" className="mt-8">
            <div className="rounded-2xl border border-warm-200 bg-white p-8 max-w-2xl">
              <h3 className="font-display text-lg font-semibold">Account settings</h3>
              <p className="text-navy-500 mt-1">Manage your profile, email, and payment details.</p>
              <div className="mt-6 space-y-4">
                <button onClick={() => setEditOpen(true)} className="w-full text-left flex items-center justify-between p-4 rounded-2xl border border-warm-200 hover:border-saffron-200 hover:bg-saffron-50/30 transition-colors">
                  <div>
                    <div className="font-medium text-navy-800">Profile information</div>
                    <div className="text-xs text-navy-500 mt-0.5">Name, bio, avatar, role</div>
                  </div>
                  <span className="text-slate-400">→</span>
                </button>
                {["Email preferences", "Payment methods", "Security & 2FA", "Danger zone"].map((s) => (
                  <button key={s} className="w-full text-left flex items-center justify-between p-4 rounded-2xl border border-warm-200 hover:border-saffron-200 hover:bg-saffron-50/30 transition-colors">
                    <span className="font-medium text-navy-800">{s}</span>
                    <span className="text-slate-400">→</span>
                  </button>
                ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
      <EditProfileModal open={editOpen} onOpenChange={setEditOpen} />
    </div>
  );
};

export default Dashboard;
